import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

type PageState = 'loading' | 'ready' | 'success' | 'invalid';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [pageState, setPageState] = useState<PageState>('loading');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Supabase puts the recovery token in the URL hash as an access_token.
  // onAuthStateChange fires a PASSWORD_RECOVERY event when the page loads
  // with a valid token, so we listen for that to confirm the link is real.
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setPageState('ready');
      }
    });

    // Safety: if no PASSWORD_RECOVERY event within 4 s, treat link as invalid/expired
    const timeout = setTimeout(() => {
      setPageState((prev) => prev === 'loading' ? 'invalid' : prev);
    }, 4000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Şifre en az 6 karakter olmalıdır.');
      return;
    }
    if (password !== confirm) {
      setError('Şifreler eşleşmiyor.');
      return;
    }

    setLoading(true);
    const { error: updateError } = await supabase.auth.updateUser({ password });
    setLoading(false);

    if (updateError) {
      setError('Şifre güncellenirken bir hata oluştu. Lütfen tekrar deneyin.');
      return;
    }

    setPageState('success');
    // Auto-redirect to login after 3 s
    setTimeout(() => navigate('/login'), 3000);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-stone-800 tracking-tight">ProLog</h1>
          <p className="text-stone-500 mt-2 text-sm">Projelerinizi organize edin</p>
        </div>

        <div className="bg-white rounded-xl p-8 shadow-sm border border-stone-200">

          {/* Loading */}
          {pageState === 'loading' && (
            <div className="flex flex-col items-center gap-4 py-4">
              <div className="w-8 h-8 border-2 border-stone-300 border-t-amber-500 rounded-full animate-spin" />
              <p className="text-stone-500 text-sm">Doğrulanıyor...</p>
            </div>
          )}

          {/* Invalid / expired link */}
          {pageState === 'invalid' && (
            <div className="text-center space-y-4">
              <div className="w-14 h-14 flex items-center justify-center bg-red-100 rounded-full mx-auto">
                <i className="ri-error-warning-line text-red-500 text-2xl" />
              </div>
              <h2 className="text-xl font-semibold text-stone-800">Geçersiz veya Süresi Dolmuş Link</h2>
              <p className="text-stone-500 text-sm">
                Şifre sıfırlama bağlantısı geçerli değil ya da süresi dolmuş.
                Lütfen yeniden şifre sıfırlama isteği gönderin.
              </p>
              <Link
                to="/forgot-password"
                className="inline-block px-4 py-2.5 bg-stone-800 text-white rounded-lg text-sm font-medium hover:bg-stone-900 transition-colors"
              >
                Yeni Link Gönder
              </Link>
            </div>
          )}

          {/* Form */}
          {pageState === 'ready' && (
            <>
              <h2 className="text-xl font-semibold text-stone-800 mb-2 text-center">Yeni Şifre Belirle</h2>
              <p className="text-stone-500 text-sm text-center mb-6">
                Hesabınız için yeni bir şifre girin.
              </p>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-stone-700 mb-1">
                    Yeni Şifre
                  </label>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    className="w-full px-4 py-2.5 rounded-lg border border-stone-300 text-stone-800 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                    placeholder="En az 6 karakter"
                  />
                </div>

                <div>
                  <label htmlFor="confirm" className="block text-sm font-medium text-stone-700 mb-1">
                    Şifre Tekrar
                  </label>
                  <input
                    id="confirm"
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-stone-300 text-stone-800 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                    placeholder="Şifreyi tekrar girin"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-2.5 bg-stone-800 text-white rounded-lg text-sm font-medium hover:bg-stone-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {loading ? 'Kaydediliyor...' : 'Şifremi Güncelle'}
                </button>
              </form>
            </>
          )}

          {/* Success */}
          {pageState === 'success' && (
            <div className="text-center space-y-4">
              <div className="w-14 h-14 flex items-center justify-center bg-green-100 rounded-full mx-auto">
                <i className="ri-checkbox-circle-line text-green-500 text-2xl" />
              </div>
              <h2 className="text-xl font-semibold text-stone-800">Şifre Güncellendi!</h2>
              <p className="text-stone-500 text-sm">
                Şifreniz başarıyla güncellendi. Birkaç saniye içinde giriş sayfasına yönlendirileceksiniz.
              </p>
              <Link
                to="/login"
                className="inline-block text-amber-600 hover:text-amber-700 text-sm font-medium"
              >
                Hemen giriş yap →
              </Link>
            </div>
          )}

          {/* Back link (shown on ready + invalid states) */}
          {(pageState === 'ready' || pageState === 'invalid') && (
            <div className="mt-6 text-center text-sm text-stone-500">
              <Link to="/login" className="text-amber-600 hover:text-amber-700 font-medium">
                Giriş sayfasına dön
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
